import { useMemo } from 'react'
import css from './views.module.css'
import { Card, Disclosure, QueryError } from '../ui'
import { useAppUsage, useAppUsageFeeds } from '../data/queries'
import { toAppUsage, usageLine, type AppUsage, type UsagePerson } from '../data/map/appUsage'
import { age } from '../lib/time'

/**
 * How the department's apps are used: which pages each employee opens, and
 * how often, over the last thirty days. Today one app, USTApp, on the dev desk.
 *
 * Renders nothing for a department no app reports to, so it sits on every desk
 * without naming one (product rule 2). A copy, and it says so: "as of" is the
 * app's last report, and a feed that has stopped arriving is marked.
 *
 * Counts, not a ranking to act on (product rule 1): no bars, no colour for a
 * heavy or a light user. Closed to its summary line by default.
 */
export function DeskAppUsage({ slug }: { slug: string }) {
  const rows = useAppUsage(slug)
  const feeds = useAppUsageFeeds(slug)
  const apps = useMemo(() => toAppUsage(rows.data ?? [], feeds.data ?? []), [rows.data, feeds.data])

  if (rows.error) return <QueryError error={rows.error} />
  if (feeds.error) return <QueryError error={feeds.error} />
  if (!rows.data || !feeds.data || apps.length === 0) return null

  return (
    <>
      {apps.map((u) => <AppCard key={u.app} usage={u} />)}
    </>
  )
}

function AppCard({ usage: u }: { usage: AppUsage }) {
  return (
    <Card style={{ marginBottom: 14 }}>
      <Disclosure
        label
        title={`${u.name} usage`}
        summary={`${usageLine(u)} · employees only · as of ${age(u.asOf)}`}
        aside={u.stale ? (
          <div className={css.ticketFail} style={{ marginTop: 0 }}>
            No report from {u.name} in over three hours, so these figures may be out of date.
          </div>
        ) : null}
      >
        {u.people.length === 0 ? (
          <p className={css.findingNone}>Nobody has opened a page in the last 30 days.</p>
        ) : (
          <>
            <div className={css.findingSubHead}>By person</div>
            {u.people.map((p) => <PersonRow key={p.userId} person={p} />)}
            <div className={css.findingSubHead}>By page</div>
            {u.pages.map((g) => (
              <div key={g.path} className={css.findingRow}>
                <div className="mono" style={{ fontSize: 12 }}>{g.views.toLocaleString('en-US')}</div>
                <div>
                  <div className={`${css.ticketTitle} mono`}>{g.path}</div>
                  <div className={`${css.ticketLanes} mono`}>
                    <span>{g.people} {g.people === 1 ? 'person' : 'people'}</span>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </Disclosure>
    </Card>
  )
}

function PersonRow({ person: p }: { person: UsagePerson }) {
  return (
    <div className={css.findingRow}>
      <div className="mono" style={{ fontSize: 12 }}>{p.views.toLocaleString('en-US')}</div>
      <div>
        <div className={css.ticketTitle}>{p.name}</div>
        <div className={`${css.ticketLanes} mono`}>
          <span>{p.role}</span>
          <span>{p.pages.length} {p.pages.length === 1 ? 'page' : 'pages'}</span>
          <span>busiest page on {p.busiestPageDays} {p.busiestPageDays === 1 ? 'day' : 'days'}</span>
          {p.lastDay ? <span>last {p.lastDay}</span> : null}
        </div>
        <details className={css.findingDetails}>
          <summary>Pages</summary>
          {p.pages.map((g) => (
            <div key={g.path} className={css.findingObject}>
              <span className="mono">{g.path}</span>
              <span className="mono" style={{ color: 'var(--ink-3)' }}>
                {g.views.toLocaleString('en-US')} {g.views === 1 ? 'view' : 'views'} · {g.daysActive} {g.daysActive === 1 ? 'day' : 'days'}
              </span>
            </div>
          ))}
        </details>
      </div>
    </div>
  )
}
